<template>
  <div class="permission-container">
    <div class="page-header">
      <div class="breadcrumb">ຈັດການລະບົບ / <span>ກຳນົດສິດການໃຊ້ງານ</span></div>
      <div class="header-content">
        <h1>ກຳນົດສິດຜູ້ໃຊ້ງານ</h1>
        <p>ຈັດການສິດທິການເຂົ້າເຖິງແຕ່ລະໂມດູນໂດຍນຳໃຊ້ລະບົບ Bitvalue</p>
      </div>
    </div>

    <div class="panel shadow-sm">
      <div class="panel-head">
        <h3><i class="fas fa-user-shield"></i> ເລືອກລະດັບຜູ້ໃຊ້ທີ່ຕ້ອງການກຳນົດສິດ</h3>
      </div>
      <div class="panel-body">
        <div class="role-selector mb-4">
          <button 
            v-for="role in roles" 
            :key="role.id" 
            :class="['role-btn', { active: selectedRoleId === role.id }]"
            @click="selectRole(role)"
          >
            <i class="fas fa-user-tag"></i> {{ role.name }}
          </button>
        </div>

        <div v-if="selectedRoleId" class="permissions-grid mt-4">
          <div v-if="loadingPermissions" class="text-center py-5">
            <i class="fas fa-spinner fa-spin fa-2x"></i>
            <p class="mt-2">ກຳລັງໂຫຼດຂໍ້ມູນສິດທິ...</p>
          </div>

          <template v-else>
          <div class="table-wrap">
            <table class="permission-table">
              <thead>
                <tr>
                  <th>ໂມດູນ (Module)</th>
                  <th class="text-center">ເຂົ້າເຖິງ (View)</th>
                  <th class="text-center">ຈັດການ (Manage)</th>
                  <th class="text-center">ລຶບ (Delete)</th>
                  <th class="text-center">Bit Value</th>
                </tr>
              </thead>
              <tbody>
                <template v-for="group in menuGroups" >
                  <!-- ແຖວຫົວຂໍ້ກຸ່ມເມນູ (Group Header Row) -->
                  <tr  :key="'group-' + group.id" class="group-header-row">
                    <td>
                      <div class="group-info">
                        <svg class="module-icon-svg" viewBox="0 0 24 24" fill="currentColor">
                          <path :d="group.icon" />
                        </svg>
                        <strong>{{ group.title }}</strong>
                        <div class="group-actions-inline ml-2">
                          <button type="button" class="btn-text-link" @click="setGroupBits(group, 7)">ເປີດທັງໝົດ</button>
                          <button type="button" class="btn-text-link secondary" @click="setGroupBits(group, 0)">ປິດ</button>
                        </div>
                      </div>
                    </td>
                    <td class="text-center"><input type="checkbox" :checked="hasBit(group.id, 0)" @change="toggleBit(group.id, 0)"></td>
                    <td class="text-center"><input type="checkbox" :checked="hasBit(group.id, 1)" @change="toggleBit(group.id, 1)"></td>
                    <td class="text-center"><input type="checkbox" :checked="hasBit(group.id, 2)" @change="toggleBit(group.id, 2)"></td>
                    
                    <td class="text-center">
                      <span class="bit-badge group-badge">{{ permissionState[group.id] || 0 }}</span>
                    </td>
                  </tr>
                  
                  <!-- ແຖວເມນູຍ່ອຍ (Submenu Item Rows) -->
                  <tr v-for="item in (group.items || [])" :key="item.id" class="item-row">
                    <td>
                      <div class="item-info">
                        <i class="fas fa-caret-right"></i>
                        <span>{{ item.title }}</span>
                      </div>
                    </td>
                    <td class="text-center">
                      <input type="checkbox" :checked="hasBit(item.id, 0)" @change="toggleBit(item.id, 0)">
                    </td>
                    <td class="text-center">
                      <input type="checkbox" :checked="hasBit(item.id, 1)" @change="toggleBit(item.id, 1)">
                    </td>
                    <td class="text-center">
                      <input type="checkbox" :checked="hasBit(item.id, 2)" @change="toggleBit(item.id, 2)">
                    </td>
                    <td class="text-center">
                      <span class="bit-badge">{{ permissionState[item.id] || 0 }}</span>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>

          <div class="form-actions-wrapper mt-4">
            <div class="total-info">
              ຄ່າລວມສິດທິທັງໝົດ: <strong>{{ totalBitValue }}</strong>
            </div>
            <button class="btn-save" @click="savePermissions" :disabled="submitting">
              <i class="fas" :class="submitting ? 'fa-spinner fa-spin' : 'fa-save'"></i>
              ບັນທຶກການຕັ້ງຄ່າ
            </button>
          </div>
          </template>
        </div>
        <div v-else class="empty-state py-5 text-center">
          <i class="fas fa-mouse-pointer fa-3x mb-3" style="opacity: 0.2"></i>
          <p>ກະລຸນາເລືອກລະດັບຜູ້ໃຊ້ເພື່ອເລີ່ມການກຳນົດສິດ</p>
        </div>
      </div>
    </div>

    <!-- Result Modal -->
    <transition name="modal">
      <div v-if="resultModal.show" class="modal-overlay" @mousedown.self="resultModal.show = false">
        <div class="custom-modal-card result-modal">
          <div class="modal-icon" :class="resultModal.type">
            <i class="fas" :class="resultModal.type === 'success' ? 'fa-check-circle' : 'fa-times-circle'"></i>
          </div>
          <div class="modal-content-area">
            <h2>{{ resultModal.title }}</h2>
            <p>{{ resultModal.message }}</p>
          </div>
          <div class="modal-footer-actions">
            <button class="btn-modal-primary" :class="resultModal.type" @click="resultModal.show = false">ເຂົ້າໃຈແລ້ວ</button>
          </div>
        </div>
      </div>
    </transition>
  
  </div>
</template>

<script>
import api from '@/utils/api';
import { menuGroups } from '@/utils/menu';

export default {
  name: 'PermissionPage',
  data() {
    return {
      loadingPermissions: false,
      submitting: false,
      selectedRoleId: null,
      roles: [
        { id: 1, name: 'Admin (ຜູ້ດູແລລະບົບ)' },
        { id: 2, name: 'Manager (ວິຊາການ)' },
        { id: 3, name: 'Teacher (ຄູສອນ)' }
      ],
      menuGroups, // ໂຄງສ້າງເມນູທັງໝົດ
      permissionState: {}, // Format: { student: 7, academic: 1, ... }
      permissionRecordId: null, // ເກັບ ID ຂອງ Record ເພື່ອໃຊ້ໃນການ Update
      resultModal: { show: false, type: 'success', title: '', message: '' }
    };
  },
  computed: {
    totalBitValue() {
      return Object.values(this.permissionState).reduce((a, b) => a + b, 0);
    }
  },
  methods: {
    selectRole(role) {
      this.selectedRoleId = role.id;
      this.fetchRolePermissions(role.id);
    },
    async fetchRolePermissions(roleId) {
      this.loadingPermissions = true;
      try {
        const res = await api.get(`/getRowsInt/permissions/role_id/${roleId}`);
        if (res.data && res.data.length > 0) {
          this.permissionState = res.data[0].permissions || {};
          this.permissionRecordId = res.data[0]._id;
        } else {
          // ຫາກບໍ່ພົບຂໍ້ມູນ ໃຫ້ຕັ້ງຄ່າເລີ່ມຕົ້ນ
          this.permissionRecordId = null;
          const defaults = {};
          this.menuGroups.forEach(group => {
            // ຕັ້ງຄ່າເລີ່ມຕົ້ນໃຫ້ກຸ່ມຫຼັກ
            defaults[group.id] = roleId === 1 ? 7 : 1;
            group.items.forEach(item => {
              // ຖ້າເປັນ Admin ໃຫ້ສິດເຕັມ (7), ຖ້າບໍ່ແມ່ນໃຫ້ສິດເບິ່ງຢ່າງດຽວ (1)
              defaults[item.id] = roleId === 1 ? 7 : 1;
            });
          });
          this.permissionState = defaults;
        }
      } catch (e) {
        console.error('Fetch permissions error:', e);
      } finally {
        this.loadingPermissions = false;
      }
    },
    hasBit(moduleKey, bit) {
      const val = this.permissionState[moduleKey] || 0;
      return (val & (1 << bit)) !== 0;
    },
    toggleBit(moduleKey, bit) {
      let currentVal = this.permissionState[moduleKey] || 0;
      currentVal ^= (1 << bit);
      this.$set(this.permissionState, moduleKey, currentVal);
    },
    setGroupBits(group, value) {
      // ອັບເດດສິດໃຫ້ກຸ່ມຫຼັກ
      this.$set(this.permissionState, group.id, value);
      // ອັບເດດສິດໃຫ້ເມນູຍ່ອຍທັງໝົດ
      group.items.forEach(item => {
        this.$set(this.permissionState, item.id, value);
      });
    },
    async savePermissions() {
      this.submitting = true;
      try {
        // ບັນທຶກລົງໃນ table 'permissions' ໂດຍໃຊ້ bitvalue ທີ່ຄຳນວນໄດ້
        const payload = {
          role_id: this.selectedRoleId,
          permissions: this.permissionState,
          total_bit: this.totalBitValue,
          updated_at: new Date()
        };
        
        let res;
        if (this.permissionRecordId) {
          res = await api.put(`/update/permissions/${this.permissionRecordId}`, { items: payload });
        } else {
          res = await api.post('/insertOne', { table: 'permissions', items: payload });
        }
        if (res.data.code === 1) {
          this.showResult('success', 'ສຳເລັດແລ້ວ', 'ບັນທຶກການກຳນົດສິດຮຽບຮ້ອຍແລ້ວ');
        }
      } catch (e) {
        this.showResult('error', 'ເກີດຂໍ້ຜິດພາດ', 'ບໍ່ສາມາດບັນທຶກສິດທິໄດ້');
      } finally {
        this.submitting = false;
      }
    },
    showResult(type, title, message) {
      this.resultModal = { show: true, type, title, message };
    }
  }
};
</script>

<style scoped>
.permission-container { animation: fadeIn 0.4s ease; max-width: 1000px; margin: 0 auto; }

.role-selector { display: flex; gap: 12px; flex-wrap: wrap; }
.role-btn {
  padding: 12px 20px; border: 1px solid var(--border); background: white;
  border-radius: 10px; cursor: pointer; transition: 0.2s; font-weight: 600;
  color: var(--text2); display: flex; align-items: center; gap: 8px;
}
.role-btn.active { background: var(--lao); color: white; border-color: var(--lao); box-shadow: 0 4px 12px rgba(0, 63, 138, 0.2); }
.role-btn:hover:not(.active) { background: #f8fafc; border-color: var(--lao); color: var(--lao); }

.permission-table { width: 100%; border-collapse: collapse; }
.permission-table th { background: #f8fafc; padding: 15px; font-size: 12px; color: var(--text3); text-transform: uppercase; border-bottom: 2px solid var(--border); }
.permission-table td { padding: 15px; border-bottom: 1px solid #edf2f7; vertical-align: middle; }

.group-header-row { background-color: #f1f5f9; }
.group-info { display: flex; align-items: center; gap: 12px; font-weight: 700; color: var(--lao); }
.group-actions-inline { display: flex; gap: 8px; font-size: 10px; }
.btn-text-link { background: none; border: none; color: var(--lao); cursor: pointer; text-decoration: underline; font-weight: 600; }
.btn-text-link.secondary { color: var(--text3); }
.ml-2 { margin-left: 8px; }

.item-row { transition: 0.2s; }
.item-row:hover { background-color: #fffbeb; }
.item-info { display: flex; align-items: center; gap: 12px; padding-left: 20px; color: var(--text2); font-size: 13.5px; }
.item-info i { font-size: 10px; opacity: 0.4; }

.group-actions { display: flex; gap: 8px; justify-content: center; }
.btn-mini-action { font-size: 10px; padding: 4px 10px; border: none; border-radius: 4px; background: var(--lao); color: white; cursor: pointer; font-weight: 600; }
.btn-mini-action.secondary { background: #cbd5e1; color: var(--text1); }

.module-icon-svg { width: 20px; height: 20px; color: var(--lao); opacity: 0.8; }

.permission-table input[type="checkbox"] {
  width: 20px; height: 20px; cursor: pointer; accent-color: var(--lao);
}

.bit-badge {
  background: #f1f5f9; color: var(--lao); padding: 4px 12px; border-radius: 6px;
  font-family: 'IBM Plex Mono', monospace; font-weight: 700; font-size: 14px;
}
.bit-badge.group-badge { background: #e2e8f0; }

.form-actions-wrapper {
  display: flex; justify-content: space-between; align-items: center;
  padding: 20px; background: #f8fafc; border-radius: 12px;
}
.total-info { font-size: 15px; color: var(--text2); }
.total-info strong { color: var(--lao); font-size: 20px; margin-left: 10px; }

.btn-save {
  background: var(--lao); color: white; border: none; padding: 12px 30px;
  border-radius: 8px; cursor: pointer; font-weight: 700; transition: 0.3s;
}
.btn-save:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0, 63, 138, 0.3); }

.empty-state { color: var(--text3); }
.text-center { text-align: center; }

/* Result Modal Styles */
.modal-overlay { position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.4); backdrop-filter: blur(4px); display: flex; justify-content: center; align-items: center; z-index: 9999; }
.custom-modal-card { background: white; width: 100%; max-width: 450px; border-radius: 16px; box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1); overflow: hidden; }
.result-modal { text-align: center; padding: 40px 30px; }
.modal-icon { font-size: 60px; margin-bottom: 20px; }
.modal-icon.success { color: #10b981; }
.modal-icon.error { color: #ef4444; }
.btn-modal-primary { padding: 12px 30px; border: none; border-radius: 8px; cursor: pointer; font-weight: 600; background: var(--lao); color: white; }

@keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
</style>